import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  Clock,
  Crown,
  TrendingUp,
  DollarSign,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { useAcademyMetrics } from "@/hooks/useAcademyMetrics";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

function formatBRL(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
}

function planBadge(plan: string, cycle: string | null) {
  if (plan === "premium") {
    return (
      <Badge className="bg-amber-500/15 text-amber-700 border border-amber-500/30">
        PRO {cycle === "yearly" ? "Anual" : "Mensal"}
      </Badge>
    );
  }
  if (plan === "trial") {
    return (
      <Badge className="bg-blue-500/15 text-blue-700 border border-blue-500/30">
        Trial
      </Badge>
    );
  }
  return <Badge variant="outline">Free</Badge>;
}

export function AcademyMetricsPanel() {
  const { data, isLoading, error } = useAcademyMetrics();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center gap-3 text-destructive">
          <AlertCircle className="h-5 w-5" />
          Erro ao carregar métricas.
        </CardContent>
      </Card>
    );
  }

  const { metrics, recent } = data;

  const kpis = [
    {
      icon: Clock,
      label: "Trials ativos",
      value: metrics.activeTrials,
      sub: `${metrics.totalTrials} totais · ${metrics.expiredTrials} expirados`,
      color: "text-blue-600 bg-blue-500/15",
    },
    {
      icon: Crown,
      label: "Assinantes PRO",
      value: metrics.totalPremium,
      sub: `${metrics.monthlyPremium} mensal · ${metrics.yearlyPremium} anual`,
      color: "text-amber-600 bg-amber-500/15",
    },
    {
      icon: TrendingUp,
      label: "Conversão Trial → PRO",
      value: `${metrics.conversionRate.toFixed(1)}%`,
      sub: "de trials que viraram assinantes",
      color: "text-emerald-600 bg-emerald-500/15",
    },
    {
      icon: DollarSign,
      label: "MRR estimado",
      value: formatBRL(metrics.estimatedMRR),
      sub: "receita recorrente mensal",
      color: "text-purple-600 bg-purple-500/15",
    },
    {
      icon: Calendar,
      label: "Novos trials (7d)",
      value: metrics.newTrialsLast7d,
      sub: `${metrics.newTrialsLast30d} nos últimos 30 dias`,
      color: "text-cyan-600 bg-cyan-500/15",
    },
    {
      icon: Users,
      label: "Novos PRO (30d)",
      value: metrics.newPremiumLast30d,
      sub: "novas assinaturas no mês",
      color: "text-pink-600 bg-pink-500/15",
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="border-border/50">
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${k.color}`}>
                <k.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold leading-tight">{k.value}</div>
                <div className="text-sm font-medium text-foreground">{k.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{k.sub}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabela recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimas 20 assinaturas / trials</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Usuário</th>
                  <th className="px-4 py-2 text-left font-semibold">Plano</th>
                  <th className="px-4 py-2 text-left font-semibold">Início</th>
                  <th className="px-4 py-2 text-left font-semibold">Fim</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      Nenhuma assinatura ainda.
                    </td>
                  </tr>
                ) : (
                  recent.map((r) => (
                    <tr key={r.user_id} className="border-t border-border">
                      <td className="px-4 py-2">
                        <div className="font-medium text-foreground">
                          {r.full_name || "Sem nome"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {r.email || r.user_id.slice(0, 8)}
                        </div>
                      </td>
                      <td className="px-4 py-2">{planBadge(r.plan_type, r.billing_cycle)}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatDate(r.premium_started_at || r.trial_started_at || r.created_at)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatDate(r.premium_ends_at || r.trial_ends_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
